function OmitEmptyParams(obj: any): Record<string, string> {
  // @ts-ignore
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value != null && value !== "" && typeof value !== "undefined"),
  );
}

// Remove undefined, null, and empty string values from the object
export const getDefinedUrlParams = (params: any): string => {
  const result = new URLSearchParams(OmitEmptyParams(params));

  return result.toString();
};

export const isUnauthorizedError = (code: number): boolean => [401, 403].includes(code);

// Check if the condition is met with a timeout
export const checkWithTimeout = (intervalMs: number, timeoutMs: number, checkFunction: Function, signal: AbortSignal) =>
  new Promise((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let intervalId: ReturnType<typeof setInterval>;

    // Function to clean up and handle the abort
    const onAbort = () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
      reject(new Error("Operation aborted"));
    };

    // If the signal is already aborted, handle it immediately
    if (signal.aborted) {
      onAbort();
      return;
    }

    // Set up the interval to check the condition
    intervalId = setInterval(() => {
      if (checkFunction()) {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
        resolve(true);
      }
    }, intervalMs);

    // Set up the timeout to reject the promise if the condition is not met
    timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      reject(new Error("Timeout reached without condition being met"));
    }, timeoutMs);

    // Attach the abort event listener
    signal.addEventListener("abort", onAbort, { once: true });
  });

export const deepEqual = (obj1: any, obj2: any) => {
  if (obj1 === obj2) return true;

  if (typeof obj1 !== "object" || obj1 === null || typeof obj2 !== "object" || obj2 === null) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  // eslint-disable-next-line no-restricted-syntax
  for (const key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
};

export const arraysEqual = (arr1: any[], arr2: any[]) => {
  if (arr1.length !== arr2.length) return false;
  return arr1.every((value, index) => deepEqual(value, arr2[index]));
};

export const formatDate = (date: string): string =>
  new Date(date).toLocaleString("en-GB", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
